<template>
    <c-stekz-tree
        ref="stekztree"
        :data-stek-id="stek?.identity"
        :data-selected-domain-id="domain?.identity ?? ''"
        :data-selected-product-id="product?.identity ?? ''"
        :data-selected-process-id="process?.identity ?? ''"
        v-if="stek && mode === 'design' && stek?.name"
    ></c-stekz-tree>
    <div>
        <Menu v-if="subStageMenuItems.length > 0" :model="subStageMenuItems" />
    </div>
    <!-- live mode content -->
    <div id="live" v-if="mode === 'live'">
        <div>L</div>
        <div>I</div>
        <div>V</div>
        <div>E</div>
    </div>

    <!-- mode -->
    <div id="mode">
        <!-- change mode button -->
        <Button
            type="button"
            :icon="modeIcon"
            class="p-button-text"
            :label="mode"
            @click="toggle($event, 'modeMenu')"
            aria-haspopup="true"
            aria-controls="overlay_modemenu"
        />
        <!-- menu items of modes -->
        <Menu
            v-if="stek?.identity"
            ref="modeMenu"
            :model="modeMenuItems"
            :popup="true"
            id="overlay_modemenu"
        />
    </div>

    <!-- Delete confirmation -->
    <ConfirmationDialog
        :displayConfirmation="!!entityToDelete"
        :displaySpinner="isLoading"
        :message="`Are you sure you want to delete ${entityToDelete?.entityType} '${entityToDelete?.name}'?`"
        autofocus="close"
        @close-confirmation="entityToDelete = null"
        @success-confirm="deleteEntity"
    />
</template>

<script setup lang="ts">
import { useOrganizationManager } from "@/composables/organization-manager";
import { useStekManager } from "@/composables/stek-manager";
import { useDomainsManager, useDomainsStore } from "@/stores/domains-store";
import { useProductsManager, useProductsStore } from "@/stores/products-store";
import {
    useProcessesManager,
    useProcessesStore,
} from "@/stores/processes-store";
import ConfirmationDialog from "@/shared/ConfirmationDialog.vue";
import { useCurrentEntityManager } from "@/stores/entity-store";
import "@/wc/stekz-tree/index.js";
import { whenever } from "@vueuse/core";
import type { MenuItem } from "primevue/menuitem";
import { computed, onMounted, ref } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useToast } from "primevue/usetoast";

const route = useRoute();
const { stek } = useStekManager();
const { organization } = useOrganizationManager();
const { domain, product, process } = useCurrentEntityManager();

const { remove: removeDomain } = useDomainsStore();
const { isLoading: isDomainsLoading } = useDomainsManager();
const { remove: removeProduct } = useProductsStore();
const { isLoading: isProductsLoading } = useProductsManager();
const { remove: removeProcess } = useProcessesStore();
const { isLoading: isProcessesLoading } = useProcessesManager();

const mode = computed(() => {
    return route.matched.find((m) => m.meta.mode)?.meta.mode || "design";
});
const modeIcon = computed(() =>
    mode.value === "live" ? "pi pi-play" : "pi pi-eye"
);

const isLoading = computed(() => {
    return (
        isDomainsLoading.value ||
        isProductsLoading.value ||
        isProcessesLoading.value
    );
});

const modeMenuItems = computed<MenuItem[]>(() => {
    if (!organization.value?.name) {
        return [];
    }

    if (!stek.value?.name) {
        return [];
    }

    return [
        {
            label: "Design",
            icon: "pi pi-eye",
            command: () => {
                router.push({
                    name: "stekPage",
                    params: {
                        orgName: organization.value?.name,
                        stekName: stek.value?.name,
                    },
                });
            },
        },
        {
            label: "Live",
            icon: "pi pi-play",
            command: () => {
                router.push({
                    name: "livePage",
                    params: {
                        orgName: organization.value?.name,
                        stekName: stek.value?.name,
                    },
                });
            },
        },
    ];
});
const subStageMenuItems = computed<MenuItem[]>(() => {
    const items: MenuItem[] = [];

    const organizationName = organization.value?.name;
    if (!organizationName) {
        return [];
    }

    const stekName = stek.value?.name;
    // To avoid router warning when there is no selected stek, only
    // add domain menu items if there is a selected stek.
    if (mode.value === "live" && stekName) {
        items.push({
            label: "Dashboard",
            icon: "pi pi-chart-bar",
            command: () => {
                router.push({
                    name: "livePage",
                    params: {
                        orgName: organizationName,
                        stekName: stekName,
                    },
                });
            },
        });
    }
    return items;
});

const menu = ref();
const modeMenu = ref();
const router = useRouter();

const entityToDelete = ref();
const stekztree = ref();

onMounted(async () => {
    // Until we have integrated inline entity creation in the tree,
    // make use of creation modals.
    whenever(
        stekztree,
        () => {
            stekztree.value.addEventListener("delete", (e: CustomEvent) => {
                entityToDelete.value = e.detail;
            });
            stekztree.value.addEventListener(
                "toast-request",
                (e: CustomEvent) => {
                    toast.add({
                        ...baseSuccessToastMessage,
                        severity: e.detail.type,
                        summary: e.detail.message,
                    });
                }
            );
        },
        { immediate: true }
    );
});

const toast = useToast();

const toggle = (event: Event, menuName: string) => {
    const activeEle = document.getElementsByClassName("active");
    if (activeEle.length > 0) {
        const activeEle = document.querySelector(".active");
        activeEle?.classList.remove("active");
    }
    switch (menuName) {
        case "menu":
            menu.value.toggle(event);
            break;
        case "modeMenu":
            modeMenu.value.toggle(event);
            break;
        default:
            // Menu not found;
            break;
    }
};

const deleteEntity = async () => {
    if (!entityToDelete.value) return;
    if (!organization.value) return;

    const baseParams = {
        orgName: organization.value?.name,
        stekName: stek.value?.name,
    };

    if (entityToDelete.value.entityType === "domain") {
        await removeDomain(entityToDelete.value.identity);
        await router.push({
            name: "stekPage",
            params: {
                ...baseParams,
            },
        });
    } else if (entityToDelete.value.entityType === "product") {
        await removeProduct(entityToDelete.value.identity);
        await router.push({
            name: "domainPage",
            params: {
                ...baseParams,
                domainId: domain.value.identity,
            },
        });
    } else if (entityToDelete.value.entityType === "process") {
        await removeProcess(entityToDelete.value.identity);
        await router.push({
            name: "productPage",
            params: {
                ...baseParams,
                productId: product.value.identity,
            },
        });
    } else {
        console.error("Unknown entity type");
        return;
    }
    entityToDelete.value = null;
};
</script>

<style scoped lang="scss">
c-stekz-tree {
    overflow-y: auto;
    display: block;
    height: 100%;
}

.profile-link {
    color: unset;
    text-decoration: inherit;
}

#mode {
    width: 160px;

    button {
        text-transform: capitalize;
    }
}

.mode-content {
    /* Make sure the menu is above the button    */
    position: absolute;
    bottom: 50px;
}

#live {
    text-align: center;
    font-size: 95px;
    font-weight: bold;
    color: var(--stekz-dark-gray);
    cursor: pointer;
}

.menu {
    border: none;
    width: fit-content;
}

.select-organization {
    text-decoration: none;
}
</style>
